import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'motion/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { createPortal } from 'react-dom'
import Thumbnail from './Thumbnail'
import { OVERLAY_FIT_FACTOR, thumbBoxSize, thumbViewBounds } from './FloatingRightRail'
import { ThumbFocusMask } from './ThumbFocusMask'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useChromeLayoutStore } from '@/hooks/useChromeLayout'
import type { Media } from '@/types/api'

/** A confirmed-positive row from mediaClasses.getGroundTruth. */
export interface GroundTruthRow {
  id: string
  media_id: string
  bbox_x1: number
  bbox_y1: number
  bbox_x2: number
  bbox_y2: number
  belongs: boolean
  inferred: boolean
}

interface GroundTruthBarProps {
  rows: GroundTruthRow[]
  medias: Media[]
  organizationId: string
  /** Fade the bar out (drawing mode), matching the other floating panels. */
  hidden?: boolean
  /** Navigate to a ground truth (deep link into its slide). */
  onItemClick?: (row: GroundTruthRow) => void
}

const THUMB = 56

const GroundTruthThumb = memo(
  function GroundTruthThumb({
    row,
    src,
    onClick,
    focusMask,
    aspect,
  }: {
    row: GroundTruthRow
    src: string
    onClick?: () => void
    focusMask?: boolean
    /** Image height / width */
    aspect: number
  }) {
    // Each thumb is a live mini tile viewer, so mount it lazily: a placeholder
    // until scrolled near the viewport, then the real viewer (which then stays).
    // This lets the bar hold hundreds of ground truths without opening
    // hundreds of viewers up front.
    const hostRef = useRef<HTMLButtonElement | null>(null)
    const [inView, setInView] = useState(false)
    useEffect(() => {
      const el = hostRef.current
      if (!el || inView) return
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) setInView(true)
        },
        { rootMargin: '0px 200px' },
      )
      observer.observe(el)
      return () => observer.disconnect()
    }, [inView])

    const bounds = { x1: row.bbox_x1, y1: row.bbox_y1, x2: row.bbox_x2, y2: row.bbox_y2 }
    const { w, h } = thumbBoxSize(bounds, aspect)
    const scale = (THUMB * OVERLAY_FIT_FACTOR) / Math.max(w, h)
    const boxW = w * scale
    const boxH = h * scale
    return (
      <button
        ref={hostRef}
        type="button"
        onClick={onClick}
        className="relative h-[56px] w-[56px] shrink-0 cursor-pointer overflow-hidden rounded-md transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent-green"
        title="Ground truth — click to view"
      >
        {inView ? (
          <Thumbnail src={src} width={THUMB} height={THUMB} initialBounds={thumbViewBounds(bounds, aspect)} />
        ) : (
          <div className="chrome-panel h-full w-full rounded-md border border-border-default/40" />
        )}
        {inView && focusMask && (
          <ThumbFocusMask
            box={{ left: (THUMB - boxW) / 2, top: (THUMB - boxH) / 2, width: boxW, height: boxH }}
            kind="gt"
          />
        )}
        {inView && !focusMask && (
          <div
            className="pointer-events-none absolute rounded-[1px]"
            style={{
              left: (THUMB - boxW) / 2,
              top: (THUMB - boxH) / 2,
              width: boxW,
              height: boxH,
              border: '1.5px solid #37c759',
              boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.35)',
            }}
          />
        )}
      </button>
    )
  },
  (prev, next) =>
    prev.row.id === next.row.id && prev.src === next.src && prev.focusMask === next.focusMask && prev.aspect === next.aspect,
)

/**
 * rc-app parity: a horizontal strip of small confirmed-positive thumbnails in
 * the header — the skill's positive examples at a glance, drawn from EVERY
 * slide in the scope (each thumb resolves its own slide's tile source).
 * Unlike rc-app's display-only bar, clicking a thumb deep-links to its slide.
 */
export function GroundTruthBar({
  rows,
  medias,
  organizationId,
  hidden,
  onItemClick,
}: GroundTruthBarProps) {
  const focusMask = useFeatureFlag('useCropMask')
  // Collapsed = a small count pill, and the header shrinks with it (see
  // useHeaderHeight). A browser preference, like the theme: IGR skills have
  // dozens of ground truths and the full strip dominates the header.
  const collapsed = useChromeLayoutStore((s) => s.gtBarCollapsed)
  const setCollapsed = useChromeLayoutStore((s) => s.setGtBarCollapsed)
  const toggleCollapsed = () => setCollapsed(!collapsed)
  // Mouse wheel scrolls the strip horizontally. Registered as a non-passive
  // native listener — React's synthetic wheel handlers can't preventDefault,
  // so the page (or viewer beneath) would consume the gesture instead. A
  // callback ref, because the strip (re)mounts when AnimatePresence finishes
  // the collapse→expand swap, which happens without this component re-rendering.
  const wheelCleanup = useRef<(() => void) | null>(null)
  const scrollRef = useCallback((el: HTMLDivElement | null) => {
    wheelCleanup.current?.()
    wheelCleanup.current = null
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
      if (delta === 0) return
      e.preventDefault()
      el.scrollLeft += delta
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    wheelCleanup.current = () => el.removeEventListener('wheel', onWheel)
  }, [])

  const positives = rows.filter((r) => !r.inferred && r.belongs)
  if (positives.length === 0) return null

  // Portaled to <body>: the page container is its own stacking context below
  // the fixed header (z-40), so anything rendered inside it sits dimmed and
  // unclickable beneath the header panel — the bar must live above it.
  const label = `${positives.length} ground truth${positives.length === 1 ? '' : 's'}`
  const pillButton =
    'flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-label-small text-text-secondary transition-colors hover:bg-surface-tertiary hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-fill-primary'

  return createPortal(
    <MotionConfig reducedMotion="user">
      <div
        // Vertically centred in whatever height the header currently has
        className="pointer-events-none fixed left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transition-[opacity,top] duration-300"
        style={{ top: 'calc(var(--header-h, 80px) / 2)', opacity: hidden ? 0 : 1 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {collapsed ? (
            <motion.button
              key="collapsed"
              type="button"
              onClick={toggleCollapsed}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              className={`chrome-panel pointer-events-auto flex h-8 items-center gap-2 rounded-modal border border-border-default/40 px-3 text-label-default text-text-primary transition-colors hover:bg-surface-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-fill-primary ${hidden ? 'pointer-events-none' : ''}`}
              title="Show ground truth examples"
              aria-expanded={false}
            >
              <span className="h-2.5 w-2.5 rounded-[3px] bg-accent-green" aria-hidden="true" />
              {label}
              <ChevronRightIcon className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
            </motion.button>
          ) : (
            <motion.div
              key="expanded"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              className={`chrome-panel flex items-center rounded-modal border border-border-default/40 p-1.5 ${hidden ? 'pointer-events-none' : 'pointer-events-auto'}`}
              style={{
                // Keep clear of the header's logo (left) and theme toggle (right) — the
                // panel's 32px shadow reached the logo on IGR slides with many ground truths
                maxWidth: 'calc(100vw - 2 * 260px)',
              }}
              aria-label={`${label} examples`}
            >
              <div
                ref={scrollRef}
                className="flex min-w-0 items-center gap-1.5 overflow-x-auto"
                style={{ scrollbarWidth: 'none' }}
              >
                {positives.map((row) => {
                  const media = medias.find((m) => m.id === row.media_id)
                  const mv = media?.media_versions?.[0]
                  const mediaVersionId = mv?.id
                  if (!mediaVersionId) return null
                  const aspect = mv.width && mv.height ? mv.height / mv.width : 1
                  return (
                    <GroundTruthThumb
                      key={row.id}
                      row={row}
                      src={`/data/${organizationId}/${mediaVersionId}/image.dzi`}
                      onClick={onItemClick ? () => onItemClick(row) : undefined}
                      focusMask={focusMask}
                      aspect={aspect}
                    />
                  )
                })}
              </div>
              <button
                type="button"
                onClick={toggleCollapsed}
                className={`${pillButton} ml-1.5`}
                title="Collapse ground truth examples"
                aria-expanded={true}
              >
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>,
    document.body,
  )
}
